import React, { useEffect, useState } from "react";
import MainLayout from "../../../layouts/main/main_layout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import {
  ArrowBack,
  BlockRounded,
  BuildOutlined,
  ChatOutlined,
  CheckCircleRounded,
  DeleteOutlineRounded,
  Directions,
  Edit,
  ListOutlined,
  PersonOutlineRounded,
  PollOutlined,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../../components/loading/loading";
import {
  appUsersService,
  deleteAppUser,
  downloadAppUsersById,
  getAppUsersById,
  getAppUsersSummaryById,
} from "../../../services/app_users/app_users";
import { useQuery } from "react-query";
import BlockAppUser from "../components/block_app_user_popup/block_app_user";
import DeleteAppUser from "../components/block_app_user_popup/block_app_user";
import { queryClient } from "../../../providers/query/query";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { AppUsersDetailsModel } from "../model/app_users_model";
import DownloadExcelButton from "../../../components/common/download_excel_button";
import { downloadFromBlob } from "../../../utils/utils";
import useUser from "../../../providers/user/user_provider";
import { modify_user } from "../../../routes/routes_path";
import FamilyMembersTab from "./components/family_members_tab";
import { black, greyEnabled, orange, orangeLightActive, purple, purpleLight } from "../../../theme/colors";
import { getMasterData } from "../../../services/choices/choices_services";

export default function UserDetails() {
  const [blockAppUserDialog, setBlockAppUserDialog] = useState<boolean>(false);
  const [deleteAppUserDialog, setDeleteAppUserDialog] =
    useState<boolean>(false);
  const [blockSubmitting, setBlockSubmitting] = useState<boolean>(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState<boolean>(false);
  const [exportSubmitting, setExportSubmitting] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = React.useState(0);
  const [modifiedSummaryDetails, setModifiedSummaryDetails] = useState([]);
  const { showSnackbar } = useFeedback();
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const { id } = useParams();

  const { data: appUsersDetails, isLoading } = useQuery<AppUsersDetailsModel>(
    ["appUsersDetails", id],
    async () => {
      let response = await getAppUsersById({
        id: +id!,
      });
      return response?.data?.data;
    }
  );

  // The backend's own "gender" string is hardcoded to English regardless of
  // locale, so resolve the display label from gender_id via the same
  // locale-aware master list used elsewhere (create/edit user, users list).
  const { data: masterDataList } = useQuery(["masterDataList"], async () => {
    let response = await getMasterData();
    return response.data.data;
  }, { staleTime: Infinity });

  const genderValue = masterDataList?.gender?.find(
    (g: { id: number; value: string }) => g.id === appUsersDetails?.gender_id
  )?.value;
  const { data: appUsersSummaryDetails } = useQuery(
    ["appUsersSummaryDetails", id],
    async () => {
      let response = await getAppUsersSummaryById({
        id: +id!,
      });
      return response?.data?.data?.data;
    }
  );

  function addingIconAndColorToSummaryDetails() {
    let modifiedObject = appUsersSummaryDetails?.list.map(
      (item: { id: number; label: string; value: string }) => {
        if (item.label === "Needs Raised") {
          return {
            ...item,
            color: "#02b2af",
            icon: <ChatOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Jobs Listed") {
          return {
            ...item,
            color: "#2e96ff",
            icon: <ChatOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Jobs Applied") {
          return {
            ...item,
            color: "#2e96ff",
            icon: <PollOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Services Listed") {
          return {
            ...item,
            color: "#60009b",
            icon: <BuildOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Schemes Enquired") {
          return {
            ...item,
            color: "#2731c8",
            icon: <ListOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Schemes Enquired for Family") {
          return {
            ...item,
            color: "#03008d",
            icon: <ListOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Choices Completed") {
          return {
            ...item,
            color: "#02b2af",
            icon: <PollOutlined sx={{ color: "#ffffff" }} />,
          };
        } else if (item.label === "Choices Completed for Family") {
          return {
            ...item,
            color: "#2e96ff",
            icon: <PollOutlined sx={{ color: "#ffffff" }} />,
          };
        }
        return {
          ...item,
          color: "#6b7280",
          icon: <ListOutlined sx={{ color: "#ffffff" }} />,
        };
      }
    );
    setModifiedSummaryDetails(modifiedObject);
  }

  function downloadExcel() {
    setExportSubmitting(true);
    downloadAppUsersById({ id: +id! })
      .then((response) => {
        downloadFromBlob(response, new Date().valueOf() + ".xlsx");
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Something went wrong while exporting the user."
        );
      })
      .finally(() => setExportSubmitting(false));
  }

  function deleteAppUserApiCall(comments: string) {
    setDeleteSubmitting(true);
    deleteAppUser({ userId: +id!, comments: comments })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("User deleted successsfully");
          queryClient.invalidateQueries({ queryKey: ["appUsersDetails"] });
          navigate(-1);
        } else {
          showSnackbar("Something went wrong");
          setDeleteSubmitting(false);
        }
      })
      .catch((e) => {
        showSnackbar("Something went wrong");
        setDeleteSubmitting(false);
      });
  }

  const updateUserAccessApiCall = (comments: string) => {
    setBlockSubmitting(true);
    appUsersService({
      comments: comments,
      isBlocked: !appUsersDetails?.isBlocked,
      userId: +id!,
    })
      .then((res) => {
        if (res.status === 200) {
          if (appUsersDetails?.isBlocked) {
            showSnackbar("User Unblocked successfully");
          } else {
            showSnackbar("User blocked successfully");
          }
          queryClient.invalidateQueries({ queryKey: ["appUsersDetails"] });
          setBlockAppUserDialog(false);
        } else {
          showSnackbar("Something went wrong");
        }
      })
      .catch((e) => {
        showSnackbar("Something went wrong");
      })
      .finally(() => {
        setBlockSubmitting(false);
      });
  };

  function updateUserAccess(comments: string) {
    updateUserAccessApiCall(comments);
  }

  function listItem({
    title,
    value,
  }: {
    title: string;
    value: any;
  }): JSX.Element {
    return (
      <Box>
        <Typography
          variant="caption"
          sx={{ color: greyEnabled, fontWeight: 600, letterSpacing: 0.2 }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: black, fontWeight: 500, mt: 0.25 }}>
          {value || "-"}
        </Typography>
      </Box>
    );
  }
  const handleChange = (event: React.SyntheticEvent, currentTabVal: number) => {
    setCurrentTab(currentTabVal);
  };

  const renderCompoBasedOnTheTab = () => {
    switch (currentTab) {
      case 0:
        return personalDetails();
      case 1:
        return locationDetails();
      case 2:
        return otherDetails();
      case 3:
        return durableDetails();
      case 4:
        // return familyMemberCard();
        return (
          <FamilyMembersTab familyMembers={appUsersDetails?.familyMember ?? []} />
        );
      case 5:
        return summary();
    }
  };

  useEffect(() => {
    addingIconAndColorToSummaryDetails();
  }, [appUsersSummaryDetails]);

  function summary() {
    return (
      <Box flex={1}>
        {/* <Box
          display="flex"
          flexDirection={"row"}
          justifyContent={"space-between"}
          mb={2}
        >
          <Typography>Need raised</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.needsRaised?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography>Job Listed</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.jobListed?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography>Job Applied</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.jobsApplied?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography>Service Listed</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.servicesListed?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography>Schemes Enquired</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.schemesEnquired?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography>Schemes Enquired For Family</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.schemesEnquiredForFamily?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography> Survey Completed</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.choicesCompleted?.toString()}
          </Typography>
        </Box>
        <Divider sx={{ backgroundColor: "#F5E6F5" }} />
        <Box display="flex" justifyContent={"space-between"} sx={{ my: 2 }}>
          <Typography> Survey Completed for family</Typography>
          <Typography fontWeight={"600"}>
            {appUsersSummaryDetails?.choicesCompletedForFamily?.toString()}
          </Typography>
        </Box> */}
        <Grid item md={12} lg={12} sm={12} xs={12}>
          <PieChart
            margin={{ left: -400 }}
            data
            series={[
              {
                arcLabel: (item) => `${item.value}`,
                arcLabelMinAngle: 45,
                data: appUsersSummaryDetails?.list || [],
                innerRadius: 30,
                outerRadius: 100,
                paddingAngle: 5,
                cornerRadius: 5,
                startAngle: 0,
                endAngle: 360,
              },
            ]}
            height={200}
            width={750}
            sx={{
              [`& .${pieArcLabelClasses.root}`]: {
                fill: "white",
                fontWeight: "bold",
              },
            }}
          />
        </Grid>
        <Grid item md={12} lg={12} sm={12} xs={12} mt={2}>
          <Grid container spacing={2}>
            {modifiedSummaryDetails?.map(
              (details: {
                id: number;
                label: string;
                value: string;
                icon: any;
                color: string;
              }) => {
                return (
                  <Grid item md={4} lg={3} sm={6} xs={12} key={details.id}>
                    <Card
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.75,
                        borderRadius: 3,
                        border: "1px solid #ECECEC",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: details.color,
                          flexShrink: 0,
                        }}
                      >
                        {details?.icon}
                      </Box>
                      <Box minWidth={0}>
                        <Typography
                          variant="caption"
                          sx={{ color: greyEnabled, fontWeight: 600 }}
                        >
                          {details?.label}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 700, color: black }}
                        >
                          {details?.value}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              }
            )}
          </Grid>
        </Grid>
      </Box>
    );
  }

  function personalDetails() {
    return (
      <>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "UID",
            value: appUsersDetails?.id,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Full Name",
            value: appUsersDetails?.name,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Date of Birth",
            value: appUsersDetails?.dob,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Gender",
            value: genderValue,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Marital Status  (Optional)",
            value: appUsersDetails?.maritalStatus,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Email  (Optional)",
            value: appUsersDetails?.emailId,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Mobile Numer",
            value: appUsersDetails?.mobileNumber,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Family Income",
            value: appUsersDetails?.familyIncome,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "ID Proof Type",
            value: appUsersDetails?.id_proof_type,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "ID Proof Number",
            value: appUsersDetails?.id_proof_number,
          })}
        </Grid>
        {appUsersDetails?.lat !== "0" && appUsersDetails?.lon !== "0" && (
          <Grid item md={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography color="primary.main">Location</Typography>
              <a
                target="_blank"
                href={`https://www.google.com/maps/dir/?api=1&destination=${appUsersDetails?.lat},${appUsersDetails?.lon}`}
                rel="noreferrer"
              >
                <Button size="small" variant="text" endIcon={<Directions />}>
                  Direction
                </Button>
              </a>
            </Box>
            <Box
              height={160}
              mt={1}
              sx={{ borderRadius: 4, overflow: "hidden" }}
            >
              <iframe
                title="map"
                width="100%"
                height="100%"
                frameBorder={0}
                src={`https://maps.google.com/maps?q=${appUsersDetails?.lat},${appUsersDetails?.lon}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
              />
            </Box>
          </Grid>
        )}
      </>
    );
  }
  function locationDetails() {
    return (
      <>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "House/Flat/Room No. (Optional)",
            value: appUsersDetails?.address_line1,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Building/Chawl Name/No. (Optional)",
            value: appUsersDetails?.address_line2,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Street/Road No./Name (Optional)",
            value: appUsersDetails?.address_line3,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Locality/Area",
            value: appUsersDetails?.area,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Landmark (Optional)",
            value: appUsersDetails?.landmark,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "State",
            value: appUsersDetails?.state,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "District",
            value: appUsersDetails?.district,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "PIN Code",
            value: appUsersDetails?.pincode,
          })}
        </Grid>
      </>
    );
  }

  function otherDetails() {
    return (
      <>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Educational Qualification",
            value: appUsersDetails?.education,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Working Status",
            value: appUsersDetails?.workingStatus,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Occupation",
            value: appUsersDetails?.occupation,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Family Type",
            value: appUsersDetails?.familyType,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Ownership of House",
            value: appUsersDetails?.houseOwnership,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Personal Income",
            value: appUsersDetails?.personalIncome,
          })}
        </Grid>
        <Grid item md={4} lg={3} sm={6} xs={12}>
          {listItem({
            title: "Family Income",
            value: appUsersDetails?.familyIncome,
          })}
        </Grid>
      </>
    );
  }

  function durableDetails() {
    return (
      <Grid item md={12} lg={12} sm={12} xs={12}>
        <Typography
          variant="caption"
          sx={{ color: greyEnabled, fontWeight: 600, letterSpacing: 0.2 }}
        >
          DURABLE DETAILS
        </Typography>
        {appUsersDetails?.durableOwnership != null &&
          appUsersDetails?.durableOwnership !== null &&
          appUsersDetails?.durableOwnership.length > 0 ? (
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {appUsersDetails?.durableOwnership?.map((durableItems) => (
              <Chip
                key={durableItems.id}
                label={durableItems?.value}
                sx={{ bgcolor: purpleLight, color: purple, fontWeight: 600 }}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ color: black, fontWeight: 500, mt: 0.25 }}>
            {"-"}
          </Typography>
        )}
      </Grid>
    );
  }

  return (
    <MainLayout>
      <Grid container spacing={3}>
        <Grid item md={12} lg={12} sm={12} xs={12}>
          <Card
            elevation={0}
            sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC" }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                  </IconButton>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: purpleLight,
                      flexShrink: 0,
                    }}
                  >
                    <PersonOutlineRounded sx={{ color: purple, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" fontWeight={700} color={black}>
                        {appUsersDetails?.name || "User Details"}
                      </Typography>
                      {!isLoading && (
                        <Chip
                          size="small"
                          icon={
                            appUsersDetails?.isBlocked ? (
                              <BlockRounded sx={{ fontSize: "16px !important" }} />
                            ) : (
                              <CheckCircleRounded sx={{ fontSize: "16px !important" }} />
                            )
                          }
                          label={appUsersDetails?.isBlocked ? "Blocked" : "Active"}
                          sx={{
                            fontWeight: 600,
                            bgcolor: appUsersDetails?.isBlocked
                              ? orangeLightActive
                              : "#E7F5EC",
                            color: appUsersDetails?.isBlocked ? orange : "#1E6446",
                            "& .MuiChip-icon": {
                              color: appUsersDetails?.isBlocked ? orange : "#1E6446",
                            },
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color={greyEnabled}>
                      UID #{appUsersDetails?.id ?? "-"}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Edit sx={{ height: 18, width: 18 }} />}
                  onClick={() => navigate(`${modify_user}/${id}`)}
                  sx={{
                    borderColor: purple,
                    color: purple,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { borderColor: purple, bgcolor: purpleLight },
                  }}
                >
                  Modify Details
                </Button>
              </Box>
              <Divider sx={{ my: 3 }} />
              {isLoading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <Loading />
                </Box>
              ) : (
                <>
                  <Tabs
                    value={currentTab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{
                      style: { backgroundColor: purple, height: 3, borderRadius: 3 },
                    }}
                  >
                    {[
                      "Personal details",
                      "Location details",
                      "Other details",
                      "Durable details",
                      "Family members",
                      "Summary",
                    ].map((label) => (
                      <Tab
                        key={label}
                        label={label}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          color: greyEnabled,
                          "&.Mui-selected": { color: purple },
                        }}
                      />
                    ))}
                  </Tabs>
                  <Box mt={3}>
                    <Grid container spacing={3} mt={1}>
                      {renderCompoBasedOnTheTab()}
                      {appUsersDetails?.systemComments && (
                        <>
                          <Box width="100%">
                            <Divider />
                          </Box>
                          <Grid item md={12} lg={12} sm={12} xs={12}>
                            {listItem({
                              title: appUsersDetails.isBlocked
                                ? "Reason for block"
                                : "Reason for unblock",
                              value: appUsersDetails?.systemComments,
                            })}
                          </Grid>
                        </>
                      )}
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={1.5}
                    >
                      <Box display="flex" gap={1.5}>
                        <Button
                          variant="outlined"
                          startIcon={<ArrowBack sx={{ height: 18, width: 18 }} />}
                          onClick={() => navigate(-1)}
                          sx={{
                            borderColor: purple,
                            color: purple,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { borderColor: purple, bgcolor: purpleLight },
                          }}
                        >
                          Back To Users
                        </Button>
                        <DownloadExcelButton onClick={downloadExcel} loading={exportSubmitting} />
                      </Box>
                      <Box display="flex" gap={1.5}>
                        <Button
                          disabled={!hasPermission("users", "delete")}
                          variant="outlined"
                          startIcon={<DeleteOutlineRounded sx={{ height: 18, width: 18 }} />}
                          onClick={() => setDeleteAppUserDialog(true)}
                          sx={{
                            minWidth: 150,
                            borderColor: purple,
                            color: purple,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { borderColor: purple, bgcolor: purpleLight },
                          }}
                        >
                          Delete User
                        </Button>
                        <Button
                          disabled={!hasPermission("users", "update")}
                          variant="contained"
                          disableElevation
                          startIcon={
                            appUsersDetails?.isBlocked ? (
                              <CheckCircleRounded sx={{ height: 18, width: 18 }} />
                            ) : (
                              <BlockRounded sx={{ height: 18, width: 18 }} />
                            )
                          }
                          onClick={() => setBlockAppUserDialog(true)}
                          sx={{
                            minWidth: 150,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            bgcolor: appUsersDetails?.isBlocked ? "#1E6446" : purple,
                            "&:hover": {
                              bgcolor: appUsersDetails?.isBlocked ? "#154A33" : "#7A2074",
                            },
                          }}
                        >
                          {appUsersDetails?.isBlocked
                            ? "Unblock user"
                            : "Block User"}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <BlockAppUser
        open={blockAppUserDialog}
        onDialogClose={() => {
          setBlockAppUserDialog(false);
        }}
        updateUserAccess={updateUserAccess}
        title={
          appUsersDetails?.isBlocked
            ? "Are you sure you want to unblock this user?"
            : "Are you sure you want to block this user?"
        }
        submitBtnLabel={appUsersDetails?.isBlocked ? "Unblock" : "Block"}
        submitting={blockSubmitting}
      />

      <DeleteAppUser
        open={deleteAppUserDialog}
        onDialogClose={() => {
          setDeleteAppUserDialog(false);
        }}
        updateUserAccess={(remarks) => {
          deleteAppUserApiCall(remarks);
        }}
        title="Are you sure you want to Delete this user?"
        submitBtnLabel={"Delete"}
        submitting={deleteSubmitting}
      />
    </MainLayout>
  );
}
